import { useTranslation } from 'react-i18next'

import { FLOATING_ELEMENT_PLACEMENT } from '../../../../../constants/common'
import Button from '../../../../Button'
import { FieldStackedLabel } from '../../../../Field'
import { FormFieldSelectNG } from '../../../../Form'
import { FormikField, getFormikFormFieldErrorProps } from '../../../../Formik'
import { ButtonSelect } from '../../../../Select'
import {
  CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES,
  CONNECTED_REQUESTS_MASS_EDIT_VALUE_LABEL_KEYS,
  CONNECTED_REQUESTS_MASS_EDIT_VALUE_PLACEHOLDER_KEYS,
  QA_NAME_DELETE_PARAMETER_BUTTON,
} from '../../ConnectedRequestsMassEditModal.constants'
import {
  getQaNameConnectedRequestsMassEditElement,
  shouldShowInvitationField,
  shouldShowValueField,
} from '../../ConnectedRequestsMassEditModal.utils'
import {
  getActionOptions,
  getAvailableFieldOptions,
  getFieldOptions,
  getParameterFieldNames,
} from '../ConnectedRequestsMassEditModalFields.utils'
import { InvitationField } from '../InvitationField'
import { ValueField } from '../ValueField'

import { useParameterRowHandlers } from './ParameterRow.hooks'

import * as styles from '../../ConnectedRequestsMassEditModal.module.css'

import DeleteIcon from '../../../../../icons/delete.svg'

function ParameterRow({
  index,
  parameter,
  parameters,
  requestId,
  onRemoveParameter,
}) {
  const { t } = useTranslation()

  const {
    actionName,
    commentName,
    commentTemplateName,
    fieldName,
    isCommentName,
    valueName,
  } = getParameterFieldNames(index)

  const selectedFieldValue =
    parameter?.[CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.field]?.value
  const selectedActionValue =
    parameter?.[CONNECTED_REQUESTS_MASS_EDIT_PARAMETER_FIELD_NAMES.action]
      ?.value

  const allFieldOptions = getFieldOptions(t)

  const actionsOptions = getActionOptions(selectedFieldValue, t)
  const fieldOptions = getAvailableFieldOptions({
    allFieldOptions,
    index,
    parameters,
  })
  const hasValueField = shouldShowValueField(selectedActionValue)

  const isDeleteDisabled = parameters.length === 1
  const deleteTooltip = isDeleteDisabled
    ? t('ConnectedRequestsMassEdit.CannotDeleteSingleParameterTooltipKey')
    : t('Modal.DeleteBtnKey')

  const {
    handleActionChange,
    handleFieldChange,
    handleRemove,
    handleResetActionAndValue,
    handleResetValue,
  } = useParameterRowHandlers({
    actionName,
    commentName,
    commentTemplateName,
    index,
    isCommentName,
    isDeleteDisabled,
    selectedActionValue,
    selectedFieldValue,
    valueName,
    onRemoveParameter,
  })

  return (
    <div className={styles.parameter}>
      <div className={styles.row}>
        <FormikField
          Component={FormFieldSelectNG}
          Field={FieldStackedLabel}
          Select={ButtonSelect}
          controlProps={{
            contentProps: {
              placeholderProps: {
                text: t('ConnectedRequestsMassEdit.ParameterPlaceholderKey'),
              },
            },
          }}
          fieldComponentProps={{
            className: styles.rowFieldStackedLabel,
            label: t('ConnectedRequestsMassEdit.ParameterLabelKey'),
          }}
          getErrorProps={getFormikFormFieldErrorProps}
          name={fieldName}
          options={fieldOptions}
          onChange={handleFieldChange}
          onClear={handleResetActionAndValue}
        />
        <FormikField
          Component={FormFieldSelectNG}
          Field={FieldStackedLabel}
          Select={ButtonSelect}
          controlProps={{
            contentProps: {
              placeholderProps: {
                text: t('ConnectedRequestsMassEdit.ActionPlaceholderKey'),
              },
            },
          }}
          disabled={!selectedFieldValue}
          fieldComponentProps={{
            className: styles.rowFieldStackedLabel,
            label: t('ConnectedRequestsMassEdit.ActionLabelKey'),
          }}
          getErrorProps={getFormikFormFieldErrorProps}
          name={actionName}
          options={actionsOptions}
          onChange={handleActionChange}
          onClear={handleResetValue}
        />
        <Button.Outlined
          isGrey
          beforeIconProps={{
            Icon: DeleteIcon,
          }}
          disabled={isDeleteDisabled}
          qaName={getQaNameConnectedRequestsMassEditElement(
            QA_NAME_DELETE_PARAMETER_BUTTON,
            index + 1
          )}
          tooltip={deleteTooltip}
          tooltipPlacement={
            isDeleteDisabled
              ? FLOATING_ELEMENT_PLACEMENT.topEnd
              : FLOATING_ELEMENT_PLACEMENT.top
          }
          onClick={handleRemove}
        />
      </div>
      {hasValueField ? (
        <div className={styles.valueRow}>
          <ValueField
            label={t(
              CONNECTED_REQUESTS_MASS_EDIT_VALUE_LABEL_KEYS[selectedFieldValue]
            )}
            name={valueName}
            placeholder={t(
              CONNECTED_REQUESTS_MASS_EDIT_VALUE_PLACEHOLDER_KEYS[
                selectedFieldValue
              ]
            )}
            selectedFieldValue={selectedFieldValue}
          />
        </div>
      ) : null}
      {shouldShowInvitationField({
        actionValue: selectedActionValue,
        fieldValue: selectedFieldValue,
      }) ? (
        <InvitationField
          commentName={commentName}
          commentTemplateName={commentTemplateName}
          index={index}
          isCommentName={isCommentName}
          parameter={parameter}
          requestId={requestId}
        />
      ) : null}
    </div>
  )
}

export default ParameterRow
